import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useState } from "react";
import { Child, Container, Header, LayerContent } from "../style";
import { TableContainer } from "../../../Users/User.style";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import useAnalytics from "../../../../../hooks/useAnalytics";
import moment from "moment";
import {
  barOptions,
  doTransformData,
  woundIncidentUnitPlaceCols,
  handlePointClicked,
  transformDataForBarChart,
  prepareTableData,
  prepareChartData
} from "../formUtils";
import CustomerLoader from "../../../../../components/CustomLoader";
import SectionModal, { DefaultContainer } from "../../wound-analysis/sections/SectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from '@progress/kendo-react-grid';
import { CenteredWholeNumCell, HeaderCenteredCell } from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import { set } from "lodash";

const WoundIncidentsByUnit = ({
  setClickedIncident,
  isFetchingSelectedIncident,
}: any) => {
  const [tableData, setTableData] = useState<any>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [modalData, setModalData] = useState<any>([]);
  const [unit, setUnit] = useState("");
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [unitDetails, setUnitDetails] = useState<any>([]);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } = useAnalyticsWrapper((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    woundFacility: state.woundFacility,
    woundAnalysisIncidents: state.woundAnalysisIncidents
  }));

  type UnitRow = {
    unit: string;
    count: number;
    pressureInjuries: number;
    skinTears: number;
    bruises: number;
    rashes: number;
    other: number;
  };

  const adjustedTableData = (unitData: any) => {
    let returnData: UnitRow[] = [];
    if (unitData.length) {
      let bruisesTotal = 0;
      let pressureTotal = 0;
      let skinTearsTotal = 0;
      let rashesTotal = 0;
      let otherTotal = 0;  
      let totalIncidents = 0;
      for (let i = 0; i < unitData.length; i++) {

        const row: UnitRow = {
          unit: unitData[i].unit,
          count: unitData[i].totalIncidents,
          pressureInjuries: unitData[i].pressureInjuries,
          skinTears: unitData[i].skinTears,
          bruises: unitData[i].bruises,
          rashes: unitData[i].rashes,
          other: unitData[i].totalIncidents - (unitData[i].pressureInjuries + unitData[i].skinTears + unitData[i].bruises + unitData[i].rashes),
        };
        returnData.push(row);

        bruisesTotal += unitData[i].bruises;
        pressureTotal += unitData[i].pressureInjuries;
        skinTearsTotal += unitData[i].skinTears;
        rashesTotal += unitData[i].rashes;
        otherTotal += row.other;
        totalIncidents += unitData[i].totalIncidents;

      }
      const totalRow: UnitRow = {
        unit: "Units Total",
        count: totalIncidents,
        pressureInjuries: pressureTotal,
        skinTears: skinTearsTotal,
        bruises: bruisesTotal,
        rashes: rashesTotal,
        other: otherTotal,
      } 
      returnData.push(totalRow);
    }
    return returnData;
  }

  useEffect(() => {
    if (woundAnalysisIncidents !== undefined) {
      const tempData = woundAnalysisIncidents?.skinIncidentsByUnit.unitBreakdown;
      const transformedData = adjustedTableData(tempData);    
      const transformedBarData = transformDataForBarChart(
        transformedData,
        "unit"
      );   
      setTableData(transformedData);
      setBarData(transformedBarData);
      setUnitDetails(woundAnalysisIncidents?.allIncidents);
    }
  }, [woundAnalysisIncidents]);
  
  const title = "Skin Incidents by Unit";

  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowUnitModal(true);
    setUnit(label);
    const filteredDetails = woundAnalysisIncidents.skinIncidentsByUnit.drillDownByUnit[label] || [];
    setModalData(filteredDetails);
  };

  useEffect(() => {
    let cellCountProps = { cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell } };
    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="scrollable"
          rows={{ data: TotalRow }}
      >
          <GridColumn field="unit" title="Unit" width="130px" />
          <GridColumn field="count" title="Incidents" width="100px" {...cellCountProps as GridColumnProps}/>
          <GridColumn field="pressureInjuries" title="Pressure Injuries" width="140px" {...cellCountProps as GridColumnProps}/>
          <GridColumn field="skinTears" title="Skin Tears" width="100px" {...cellCountProps as GridColumnProps}/>
          <GridColumn field="bruises" title="Bruises" width="95px" {...cellCountProps as GridColumnProps}/>
          <GridColumn field="rashes" title="Rashes" width="95px" {...cellCountProps as GridColumnProps}/>
          <GridColumn field="other" title="Other" width="95px" {...cellCountProps as GridColumnProps}/>
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]); 

   return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="60%">
          <TableContainer>{grid}</TableContainer>
        </Child>
        <Child flex="40%">
          <ChartContainer>
            {barData?.labels?.length > 0 ? (
              <Bar options={{ ...barOptions(handleBarClicked), aspectRatio: 1.1 }} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>

      {showUnitModal ? (
        <SectionModal
          open={showUnitModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowUnitModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={modalData}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${unit}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData,
              columnData: woundAnalysisDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${unit}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              unitDetails,
              woundAnalysisDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${unit}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default WoundIncidentsByUnit;